import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";
import "@/i18n"; // boots react-i18next

import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { initNativeMobile } from "@/mobile";

// Boot native push registration when running inside Capacitor shell.
// No-op on plain web.
initNativeMobile();

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import ImpactPage from "@/pages/ImpactPage";
import DashboardLayout from "@/components/DashboardLayout";

// Public marketing pages (SEO-indexable)
import SolarPage from "@/pages/marketing/SolarPage";
import ElectricalPage from "@/pages/marketing/ElectricalPage";
import MaintenancePage from "@/pages/marketing/MaintenancePage";
import AboutPage from "@/pages/marketing/AboutPage";
import ContactPage from "@/pages/marketing/ContactPage";
import NotFoundPage from "@/pages/marketing/NotFoundPage";

// Client
import ClientOverview from "@/pages/client/ClientOverview";
import MyProjects from "@/pages/client/MyProjects";
import SolarPackages from "@/pages/client/SolarPackages";
import ServiceRequest from "@/pages/client/ServiceRequest";
import MyInvoices from "@/pages/client/MyInvoices";
import { PaymentsPage, PaymentStatusPage } from "@/pages/client/Payments";
import ClientMessages from "@/pages/client/ClientMessages";
import ActivityLog from "@/pages/client/ActivityLog";
import CalculatorPage from "@/pages/client/CalculatorPage";
import ProfilePage from "@/pages/ProfilePage";

// Admin
import { AdminClients } from "@/pages/admin/AdminClients";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminRequests from "@/pages/admin/AdminRequests";
import AdminProjectDetail from "@/pages/admin/AdminProjectDetail";
import AdminProjectHandoff from "@/pages/admin/AdminProjectHandoff";
import { AdminEmployees, AdminWorkOrders } from "@/pages/admin/AdminEmployees";
import AdminProjectMessages from "@/pages/admin/AdminProjectMessages";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminFinances from "@/pages/admin/AdminFinances";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminInvoiceDetail from "@/pages/admin/AdminInvoiceDetail";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminEstimates, { AdminEstimateDetail } from "@/pages/admin/AdminEstimates";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminSalesPipeline from "@/pages/admin/AdminSalesPipeline";
import PackageCompare from "@/pages/client/PackageCompare";
import AdminPipeline from "@/pages/admin/AdminPipeline";
import AdminDispatch from "@/pages/admin/AdminDispatch";
import AdminProjectStages from "@/pages/admin/AdminProjectStages";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminPurchaseOrders from "@/pages/admin/AdminPurchaseOrders";
import AdminInventoryForecast from "@/pages/admin/AdminInventoryForecast";
import AdminEmails from "@/pages/admin/AdminEmails";
import AdminChartOfAccounts from "@/pages/admin/AdminChartOfAccounts";
import AdminJournalEntries from "@/pages/admin/AdminJournalEntries";
import AdminTotReport from "@/pages/admin/AdminTotReport";
import AdminProjectProfit from "@/pages/admin/AdminProjectProfit";
import AdminBankRecon from "@/pages/admin/AdminBankRecon";
import AdminTimesheets from "@/pages/admin/AdminTimesheets";
import AdminExecutive from "@/pages/admin/AdminExecutive";
import AdminToday from "@/pages/admin/AdminToday";
import AdminDataIntegrity from "@/pages/admin/AdminDataIntegrity";
import AdminApprovals from "@/pages/admin/AdminApprovals";
import AdminExpansions from "@/pages/admin/AdminExpansions";
import MySystems from "@/pages/client/MySystems";
import AdminPnL from "@/pages/admin/AdminPnL";
import ClientHandoff, { ClientHandoffDetail } from "@/pages/client/ClientHandoff";
import SolarConfigurator from "@/components/SolarConfigurator";
import { useParams } from "react-router-dom";

// Public (no-auth) pages
import PublicEstimateSign from "@/pages/PublicEstimateSign";
import { ClientEstimatesList, ClientEstimateDetail } from "@/pages/client/ClientEstimates";

function AdminEstimateDetailRoute() {
  const { id } = useParams();
  return <AdminEstimateDetail estimateId={id} />;
}

// Employee
import EmployeeWorkOrders from "@/pages/employee/EmployeeWorkOrders";
import EmployeeToday from "@/pages/employee/EmployeeToday";
import EmployeeDailyLog from "@/pages/employee/EmployeeDailyLog";
import NotificationsPage from "@/pages/NotificationsPage";
import AdminMaterials from "@/pages/admin/AdminMaterials";
import AdminPayroll from "@/pages/admin/AdminPayroll";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAccessControl from "@/pages/admin/AdminAccessControl";
import AdminLeave from "@/pages/admin/AdminLeave";
import EmployeeLeave from "@/pages/employee/EmployeeLeave";
import SignPage from "@/pages/SignPage";
import NPSPrompt from "@/components/NPSPrompt";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import GlobalSearch from "@/components/GlobalSearch";

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-[#F8FAFC]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" || user.role === "super_admin") return <Navigate to="/admin" replace />;
  if (user.role === "employee") return <Navigate to="/employee" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRouter() {
  const location = useLocation();
  const [searchOpen, setSearchOpen] = React.useState(false);

  React.useEffect(() => {
    const onKey = (e) => {
      // Cmd+K (Mac) or Ctrl+K (Win/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((x) => !x);
      } else if (e.key === "Escape") {
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Synchronous detection of OAuth callback hash
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/solar" element={<SolarPage />} />
      <Route path="/electrical" element={<ElectricalPage />} />
      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/impact" element={<ImpactPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/me" element={<RoleRedirect />} />
      <Route path="/sign/estimate/:token" element={<PublicEstimateSign />} />
      <Route path="/sign/:token" element={<SignPage />} />
      <Route path="/configurator" element={<SolarConfigurator />} />

      {/* Client area */}
      <Route path="/dashboard" element={<DashboardLayout />}>
        <Route index element={<ClientOverview />} />
        <Route path="projects" element={<MyProjects />} />
        <Route path="systems" element={<MySystems />} />
        <Route path="calculator" element={<CalculatorPage />} />
        <Route path="packages" element={<SolarPackages />} />
        <Route path="compare" element={<PackageCompare />} />
        <Route path="services" element={<ServiceRequest />} />
        <Route path="invoices" element={<MyInvoices />} />
        <Route path="invoices/:id" element={<MyInvoices />} />
        <Route path="estimates" element={<ClientEstimatesList />} />
        <Route path="estimates/:id" element={<ClientEstimateDetail />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="payment-status" element={<PaymentStatusPage />} />
        <Route path="messages" element={<ClientMessages />} />
        <Route path="activity" element={<ActivityLog />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="handoff" element={<ClientHandoff />} />
        <Route path="handoff/:id" element={<ClientHandoffDetail />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Admin area */}
      <Route path="/admin" element={<DashboardLayout />}>
        <Route index element={<Navigate to="/admin/today" replace />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="clients/:id" element={<AdminClientDetail />} />
        <Route path="employees" element={<AdminEmployees />} />
        <Route path="projects" element={<AdminProjects />} />
        <Route path="projects/:id" element={<AdminProjectDetail />} />
        <Route path="projects/:id/handoff" element={<AdminProjectHandoff />} />
        <Route path="project-stages" element={<AdminProjectStages />} />
        <Route path="requests" element={<AdminRequests />} />
        <Route path="work-orders" element={<AdminWorkOrders />} />
        <Route path="project-messages" element={<AdminProjectMessages />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="invoices/:id" element={<AdminInvoiceDetail />} />
        <Route path="estimates" element={<AdminEstimates />} />
        <Route path="estimates/:id" element={<AdminEstimateDetailRoute />} />
        <Route path="packages" element={<AdminPackages />} />
        <Route path="sales-pipeline" element={<AdminSalesPipeline />} />
        <Route path="pipeline" element={<AdminPipeline />} />
        <Route path="dispatch" element={<AdminDispatch />} />
        <Route path="testimonials" element={<AdminTestimonials />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="finances" element={<AdminFinances />} />
        <Route path="chart-of-accounts" element={<AdminChartOfAccounts />} />
        <Route path="journal-entries" element={<AdminJournalEntries />} />
        <Route path="tot-report" element={<AdminTotReport />} />
        <Route path="project-profit" element={<AdminProjectProfit />} />
        <Route path="bank-recon" element={<AdminBankRecon />} />
        <Route path="timesheets" element={<AdminTimesheets />} />
        <Route path="executive" element={<AdminExecutive />} />
        <Route path="today" element={<AdminToday />} />
        <Route path="data-integrity" element={<AdminDataIntegrity />} />
        <Route path="approvals" element={<AdminApprovals />} />
        <Route path="expansions" element={<AdminExpansions />} />
        <Route path="pnl" element={<AdminPnL />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="materials" element={<AdminMaterials />} />
        <Route path="suppliers" element={<AdminSuppliers />} />
        <Route path="purchase-orders" element={<AdminPurchaseOrders />} />
        <Route path="inventory-forecast" element={<AdminInventoryForecast />} />
        <Route path="emails" element={<AdminEmails />} />
        <Route path="payroll" element={<AdminPayroll />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="access-control" element={<AdminAccessControl />} />
        <Route path="leave" element={<AdminLeave />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      {/* Employee area */}
      <Route path="/employee" element={<DashboardLayout />}>
        <Route index element={<EmployeeToday />} />
        <Route path="work-orders" element={<EmployeeWorkOrders />} />
        <Route path="daily-log" element={<EmployeeDailyLog />} />
        <Route path="leave" element={<EmployeeLeave />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    <NPSPrompt />
    <PWAInstallPrompt />
    <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <AppRouter />
              <Toaster
                position="bottom-right"
                closeButton
                expand={false}
                toastOptions={{
                  duration: 5000,
                  classNames: {
                    toast:
                      "!rounded-lg !border !shadow-[0_10px_28px_-8px_rgba(10,37,64,0.35)] !bg-white dark:!bg-slate-900 !text-[#0A2540] dark:!text-white !border-[#E2E8F0] dark:!border-slate-700 !p-4",
                    title: "!font-semibold !text-sm",
                    description: "!text-xs !text-[#475569] dark:!text-slate-400",
                    success: "!border-l-4 !border-l-emerald-500",
                    error:   "!border-l-4 !border-l-red-500",
                    info:    "!border-l-4 !border-l-[#C9A356]",
                    warning: "!border-l-4 !border-l-amber-500",
                    closeButton: "!bg-transparent !text-[#94A3B8] hover:!text-[#0A2540]",
                  },
                }}
              />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
